import axios, { AxiosError } from "axios";
import { useContext } from "react";
import useSWR from "swr";
import moment from "moment";
import { ethers } from "ethers";
import { AuthContext } from "../../../context/AuthContext";
import { TRADES_API_URL } from "../../../utils/env";
import { FilledTradesEndpointEnum } from "../../../enums/endpoints";

export interface IStakedAccountVolume {
  Account: string;
  TotalVolume: string;
}

interface IStakedAccountTradesResponse {
  success: boolean;
  message: string;
  data: IStakedAccountVolume[] | { data: IStakedAccountVolume[] };
}

// Epoch 1 starts from 1765180800 (seconds), Monday 08:00 UTC
// Convert to milliseconds for comparison with Date.now()
const EPOCH_1_START_MS = 1765180800 * 1000;
const EPOCH_DURATION_MS = 7 * 24 * 60 * 60 * 1000; // 1 week in milliseconds

// Get Monday 08:00 UTC time range for a specific epoch
const getEpochTimeRange = (epoch: number): { start: number; end: number } => {
  // Calculate start time for the epoch
  const epochStartMs = EPOCH_1_START_MS + (epoch - 1) * EPOCH_DURATION_MS;
  const epochEndMs = epochStartMs + EPOCH_DURATION_MS;

  // Convert to nanoseconds (multiply milliseconds by 1e6)
  return {
    start: epochStartMs * 1e6,
    end: epochEndMs * 1e6,
  };
};

// Get Monday 08:00 UTC before and after current time
const getMonday8AMUTC = (
  currentTime: number
): { start: number; end: number } => {
  const now = moment.utc(currentTime);

  // Find the most recent Monday 08:00 UTC
  let mondayBefore = now
    .clone()
    .startOf("isoWeek")
    .hour(8)
    .minute(0)
    .second(0)
    .millisecond(0);

  // If current time is before Monday 08:00 this week, go to previous Monday
  if (now.isBefore(mondayBefore)) {
    mondayBefore = mondayBefore.subtract(1, "week");
  }

  // Monday 08:00 UTC after current time (next Monday)
  const mondayAfter = mondayBefore.clone().add(1, "week");

  // Convert to nanoseconds (multiply milliseconds by 1e6)
  return {
    start: mondayBefore.valueOf() * 1e6,
    end: mondayAfter.valueOf() * 1e6,
  };
};

export const useStakedAccountTrades = (
  stakedAccounts: string[],
  epoch?: number
) => {
  const { tokenId } = useContext(AuthContext);

  const fetchStakedAccountTrades = async (): Promise<
    IStakedAccountVolume[] | undefined
  > => {
    if (!tokenId || !stakedAccounts || stakedAccounts.length === 0) {
      return undefined;
    }

    try {
      // Use epoch-specific time range if epoch is provided, otherwise use current week
      const { start, end } = epoch
        ? getEpochTimeRange(epoch)
        : getMonday8AMUTC(Date.now());

      // Convert lowercase addresses from subgraph to checksum addresses for API
      const checksummedAccounts = stakedAccounts.map((addr) => {
        try {
          return ethers.utils.getAddress(addr);
        } catch {
          return addr; // Return as-is if not a valid address
        }
      });
      const accountsParam = checksummedAccounts.join(",");

      const response = await axios.get<IStakedAccountTradesResponse>(
        TRADES_API_URL(FilledTradesEndpointEnum.STAKERS_TRADES),
        {
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${tokenId}`,
            "Access-Control-Allow-Origin": "*",
          },
          params: {
            staked_accounts: accountsParam,
            start: start.toString(),
            endTime: end.toString(),
          },
        }
      );

      if (response.data.success && response.data.data) {
        // Handle different response structures
        if (Array.isArray(response.data.data)) {
          return response.data.data;
        }
        if (
          typeof response.data.data === "object" &&
          "data" in response.data.data
        ) {
          return (response.data.data as { data: IStakedAccountVolume[] }).data;
        }
      }

      return [];
    } catch (error) {
      // do nothing
      return undefined;
    }
  };

  const swr = useSWR<IStakedAccountVolume[] | undefined, AxiosError>(
    tokenId && stakedAccounts.length > 0
      ? ["staked-account-trades", tokenId, stakedAccounts.join(","), epoch]
      : null,
    fetchStakedAccountTrades,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      dedupingInterval: 60000, // Cache for 1 minute
      refreshInterval: 300000, // Refresh every 5 minutes
    }
  );

  return {
    ...swr,
  };
};
